import React from "react";
import Header from "@/components/layout/Header";
import Footer from "@/components/layout/Footer";
import BackToTop from "@/components/ui/BackToTop";
import ContactForm from "@/components/contact/ContactForm";
import {
  Mail,
  Phone,
  MapPin,
  Clock,
  MessageCircle,
  HelpCircle,
} from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";

const Contact = () => {
  const contactMethods = [
    {
      icon: MessageCircle,
      title: "General Support",
      primary: "Use our contact form",
      secondary: "General inquiries and technical support",
      action: "/contact?category=support",
    },
    {
      icon: Mail,
      title: "Media & Press",
      primary: "Press inquiries form",
      secondary: "Media inquiries and press requests",
      action: "/contact?category=media",
    },
    {
      icon: Mail,
      title: "Partnerships",
      primary: "Partnership application",
      secondary: "Business partnerships and collaborations",
      action: "/contact?category=partnerships",
    },
  ];

  const faqItems = [
    {
      question: "How do I become a verified pollster?",
      answer:
        "You can apply to become a verified pollster by completing your profile with identity verification documents and demographic information.",
    },
    {
      question: "Is my data secure?",
      answer:
        "Yes, we take data security seriously. All information is encrypted and we follow strict privacy guidelines in compliance with GDPR.",
    },
    {
      question: "How are poll results calculated?",
      answer:
        "Poll results are calculated in real-time based on verified voter responses. We use statistical weighting to ensure representative results.",
    },
    {
      question: "Can I suggest new polls?",
      answer:
        "Absolutely! You can suggest new poll topics through our poll suggestion feature available on country-specific pages.",
    },
  ];

  return (
    <div className="min-h-screen flex flex-col bg-background">
      <Header />

      <main className="flex-1 pt-[84px]">
        {/* Hero Section */}
        <section className="relative py-24 px-4 bg-gradient-to-br from-nation-blue-dark via-nation-purple-dark to-nation-teal-dark overflow-hidden">
          <div className="absolute inset-0 bg-gradient-to-b from-blue-900/40 to-purple-900/60 backdrop-blur-sm"></div>
          <div className="absolute inset-0">
            <div className="absolute top-20 left-20 w-32 h-32 bg-gradient-to-r from-yellow-400/30 to-orange-500/30 rounded-full blur-xl animate-pulse"></div>
            <div className="absolute bottom-20 right-20 w-40 h-40 bg-gradient-to-r from-cyan-400/30 to-blue-500/30 rounded-full blur-xl animate-pulse delay-1000"></div>
            <div className="absolute top-1/2 left-1/3 w-24 h-24 bg-gradient-to-r from-pink-400/30 to-purple-500/30 rounded-full blur-xl animate-pulse delay-500"></div>
          </div>

          <div className="container mx-auto max-w-4xl text-center relative z-10">
            <div className="inline-flex items-center gap-2 px-6 py-3 bg-white/10 backdrop-blur-md rounded-full mb-8 border border-white/20">
              <MessageCircle className="h-5 w-5 text-yellow-300" />
              <span className="text-sm font-semibold text-white">
                Get in Touch
              </span>
            </div>

            <h1 className="text-5xl md:text-6xl lg:text-7xl font-bold mb-8 text-white leading-tight">
              Contact <span className="text-yellow-300">NationCard™</span>
            </h1>

            <p className="text-xl md:text-2xl text-white/90 mb-12 max-w-3xl mx-auto leading-relaxed">
              Have questions, feedback, or want to learn more about our
              platform? We're here to help and would love to hear from you.
            </p>

            <div className="flex flex-wrap justify-center gap-4">
              <div className="bg-white/10 backdrop-blur-md rounded-lg px-6 py-3 border border-white/20">
                <div className="flex items-center gap-2">
                  <Clock className="h-4 w-4 text-green-300" />
                  <span className="text-white/90 text-sm">
                    24-48 hour response
                  </span>
                </div>
              </div>
              <div className="bg-white/10 backdrop-blur-md rounded-lg px-6 py-3 border border-white/20">
                <div className="flex items-center gap-2">
                  <MapPin className="h-4 w-4 text-blue-300" />
                  <span className="text-white/90 text-sm">Global platform</span>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Main Content */}
        <section className="py-24 px-4 bg-gradient-to-b from-background to-secondary/20">
          <div className="container mx-auto max-w-7xl">
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-12">
              {/* Contact Form */}
              <div className="lg:col-span-2">
                <div className="relative">
                  <div className="absolute -inset-4 bg-gradient-to-r from-nation-purple/20 via-nation-teal/20 to-nation-blue/20 rounded-2xl blur-xl"></div>
                  <div className="relative">
                    <ContactForm />
                  </div>
                </div>
              </div>

              {/* Contact Information */}
              <div className="space-y-8">
                {/* Contact Methods */}
                <Card className="bg-gradient-to-br from-white/80 to-blue-50/80 dark:from-card/80 dark:to-blue-950/80 backdrop-blur-sm border border-blue-200/50 dark:border-blue-800/50 shadow-xl hover:shadow-2xl transition-all duration-300">
                  <CardHeader>
                    <CardTitle className="flex items-center gap-3">
                      <div className="p-2 bg-blue-500 rounded-lg">
                        <Mail className="h-6 w-6 text-white" />
                      </div>
                      <span className="text-foreground">
                        Contact Information
                      </span>
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-6">
                    {contactMethods.map((method, index) => (
                      <div
                        key={index}
                        className="group flex items-start gap-3 sm:gap-4 p-3 sm:p-4 rounded-xl bg-gradient-to-r from-transparent to-blue-50/50 dark:to-blue-950/50 hover:from-blue-50/50 hover:to-purple-50/50 dark:hover:from-blue-950/50 dark:hover:to-purple-950/50 transition-all duration-300"
                      >
                        <div className="p-2 sm:p-3 bg-blue-500 rounded-xl shadow-lg group-hover:shadow-xl group-hover:scale-110 transition-all duration-300 flex-shrink-0">
                          <method.icon className="h-4 w-4 sm:h-6 sm:w-6 text-white" />
                        </div>
                        <div className="flex-1 min-w-0 overflow-hidden">
                          <h3 className="font-bold text-foreground text-base sm:text-lg mb-1 sm:mb-2 break-words overflow-wrap-anywhere">
                            {method.title}
                          </h3>
                          <Button
                            variant="link"
                            className="p-0 h-auto font-semibold text-sm sm:text-base text-blue-600 hover:text-blue-700 transition-colors duration-300 break-all overflow-wrap-anywhere text-left justify-start"
                            onClick={() =>
                              (window.location.href = method.action)
                            }
                          >
                            <span className="break-all overflow-wrap-anywhere">
                              {method.primary}
                            </span>
                          </Button>
                          <p className="text-muted-foreground text-xs sm:text-sm mt-1 sm:mt-2 leading-relaxed break-words overflow-wrap-anywhere">
                            {method.secondary}
                          </p>
                        </div>
                      </div>
                    ))}
                  </CardContent>
                </Card>

                {/* Response Time */}
                <Card className="bg-gradient-to-br from-white/80 to-green-50/80 dark:from-card/80 dark:to-green-950/80 backdrop-blur-sm border border-green-200/50 dark:border-green-800/50 shadow-xl hover:shadow-2xl transition-all duration-300 hover:scale-105">
                  <CardHeader>
                    <CardTitle className="flex items-center gap-3">
                      <div className="p-2 bg-green-500 rounded-lg">
                        <Clock className="h-6 w-6 text-white" />
                      </div>
                      <span className="text-foreground">Response Time</span>
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <p className="text-muted-foreground leading-relaxed">
                      We typically respond to all inquiries within{" "}
                      <span className="font-bold text-green-600">
                        24-48 hours
                      </span>{" "}
                      during business days. For urgent matters, please include{" "}
                      <span className="font-bold text-red-500">"URGENT"</span>{" "}
                      in your subject line.
                    </p>
                  </CardContent>
                </Card>

                {/* Office Information */}
                <Card className="bg-gradient-to-br from-white/80 to-purple-50/80 dark:from-card/80 dark:to-purple-950/80 backdrop-blur-sm border border-purple-200/50 dark:border-purple-800/50 shadow-xl hover:shadow-2xl transition-all duration-300 hover:scale-105">
                  <CardHeader>
                    <CardTitle className="flex items-center gap-3">
                      <div className="p-2 bg-purple-500 rounded-lg">
                        <MapPin className="h-6 w-6 text-white" />
                      </div>
                      <span className="text-foreground">Global Platform</span>
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <p className="text-muted-foreground leading-relaxed">
                      NationCard™ operates as a{" "}
                      <span className="font-bold text-purple-600">
                        global digital platform
                      </span>
                      , connecting communities worldwide. We're building bridges
                      between citizens and democracy, one poll at a time.
                    </p>
                  </CardContent>
                </Card>
              </div>
            </div>
          </div>
        </section>

        {/* FAQ Section */}
        <section className="py-24 px-4 bg-gradient-to-br from-purple-50/50 via-blue-50/30 to-teal-50/50 dark:from-purple-950/20 dark:via-blue-950/20 dark:to-teal-950/20 relative overflow-hidden">
          <div className="absolute inset-0">
            <div className="absolute top-10 left-10 w-32 h-32 bg-gradient-to-r from-purple-400/20 to-pink-400/20 rounded-full blur-xl animate-pulse"></div>
            <div className="absolute bottom-10 right-10 w-40 h-40 bg-gradient-to-r from-blue-400/20 to-teal-400/20 rounded-full blur-xl animate-pulse delay-1000"></div>
          </div>

          <div className="container mx-auto max-w-4xl relative z-10">
            <div className="text-center mb-16">
              <div className="inline-flex items-center gap-3 px-6 py-3 bg-black/80 dark:bg-black/20 backdrop-blur-md rounded-full mb-8 border border-black/50 dark:border-white/10">
                <div className="p-1 bg-yellow-500 rounded-full">
                  <HelpCircle className="h-5 w-5 text-white" />
                </div>
                <span className="text-sm font-semibold text-white dark:text-white">
                  Frequently Asked Questions
                </span>
              </div>

              <h2 className="text-4xl md:text-5xl font-bold mb-6 text-foreground">
                Common Questions
              </h2>

              <p className="text-xl text-muted-foreground leading-relaxed">
                Find quick answers to the most common questions about
                NationCard™
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
              {faqItems.map((item, index) => (
                <Card
                  key={index}
                  className="group bg-white/80 dark:bg-card/80 backdrop-blur-sm border border-white/20 dark:border-border/50 shadow-xl hover:shadow-2xl transition-all duration-500 hover:scale-105 hover:rotate-1 relative overflow-hidden"
                >
                  <div className="absolute inset-0 bg-gradient-to-br from-transparent via-transparent to-purple-50/50 dark:to-purple-950/20 opacity-0 group-hover:opacity-100 transition-opacity duration-500"></div>
                  <CardHeader className="relative z-10">
                    <CardTitle className="text-lg leading-relaxed group-hover:text-purple-600 dark:group-hover:text-purple-400 transition-colors duration-300">
                      {item.question}
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="relative z-10">
                    <p className="text-muted-foreground leading-relaxed">
                      {item.answer}
                    </p>
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>
        </section>

        {/* CTA Section */}
        <section className="py-24 px-4 bg-gradient-to-br from-nation-blue-dark via-nation-purple-dark to-nation-teal-dark relative overflow-hidden">
          <div className="absolute inset-0 bg-gradient-to-r from-blue-900/60 via-purple-900/60 to-teal-900/60"></div>
          <div className="absolute inset-0">
            <div className="absolute top-20 left-20 w-32 h-32 bg-gradient-to-r from-yellow-400/40 to-orange-500/40 rounded-full blur-xl animate-pulse"></div>
            <div className="absolute bottom-20 right-20 w-40 h-40 bg-gradient-to-r from-cyan-400/40 to-blue-500/40 rounded-full blur-xl animate-pulse delay-1000"></div>
            <div className="absolute top-1/2 left-1/2 w-24 h-24 bg-gradient-to-r from-pink-400/40 to-purple-500/40 rounded-full blur-xl animate-pulse delay-500"></div>
          </div>

          <div className="container mx-auto max-w-4xl text-center relative z-10">
            <h2 className="text-4xl md:text-5xl font-bold mb-8 text-white">
              Ready to Get Started?
            </h2>

            <p className="text-xl text-white/90 mb-12 max-w-2xl mx-auto leading-relaxed">
              Join our community of verified pollsters and help shape the future
              of democratic engagement.
            </p>

            <div className="flex flex-col sm:flex-row gap-6 justify-center">
              <Button
                size="lg"
                className="group bg-gradient-to-r from-yellow-400 via-orange-500 to-red-500 hover:from-yellow-300 hover:via-orange-400 hover:to-red-400 text-white font-bold shadow-2xl hover:shadow-3xl transition-all duration-300 hover:scale-110 border-0 px-8 py-4 text-lg"
                onClick={() => (window.location.href = "/auth")}
              >
                <span className="flex items-center gap-2">
                  Sign Up Today
                  <div className="w-2 h-2 bg-white rounded-full animate-pulse"></div>
                </span>
              </Button>

              <Button
                size="lg"
                className="group bg-white/10 backdrop-blur-md hover:bg-white/20 text-white border border-white/30 hover:border-white/50 font-semibold shadow-xl hover:shadow-2xl transition-all duration-300 hover:scale-110 px-8 py-4 text-lg"
                onClick={() => (window.location.href = "/polls")}
              >
                <span className="flex items-center gap-2">
                  Explore Polls
                  <div className="w-2 h-2 bg-cyan-300 rounded-full animate-pulse delay-300"></div>
                </span>
              </Button>
            </div>
          </div>
        </section>
      </main>

      <Footer />
      <BackToTop />
    </div>
  );
};

export default Contact;
